import React, {useState} from 'react';
import {Pressable, StyleSheet, View, type StyleProp, type ViewStyle} from 'react-native';
import {useTranslation} from 'react-i18next';

import {Icon} from '@/shared/ui/icons';
import {Sheet} from '@/shared/ui/sheet';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme, withAlpha} from '@/shared/ui/theme';

/**
 * "What does this number actually mean?" — answered in place, next to the number.
 *
 * A report is the one screen in this app where the user is not doing anything: they
 * are READING, and every unexplained word is a question they have to take somewhere
 * else (a colleague, a support chat, or an assumption). «Явка 87%» invites four
 * different guesses about the denominator, and «Неполный день» invites six about what
 * counts as one. The screens used to answer none of them.
 *
 * So every block of figures carries this mark, and pressing it opens the definitions
 * for exactly that block — the term, what it means in one sentence, and, where the
 * figure is computed rather than counted, the arithmetic in the same words the screen
 * uses. That is deliberately not a tooltip: a tooltip has no touch equivalent, and half
 * this app's traffic is a phone.
 *
 * The mark itself is a 20pt ring, not a full IconButton: it sits INSIDE a heading row
 * beside a title, and a 36pt button there reads as an action the section can be made
 * to do. Its hit area is widened with `hitSlop` instead, so the thumb target stays 44pt
 * while the ink stays small.
 */
export type Definition = {
  /** The word as it appears on screen — must match it exactly, or it explains nothing. */
  term: string;
  /** One sentence. If it needs two, the metric is probably two metrics. */
  meaning: string;
  /** How it is computed. Only for figures the app derives; a counter has none. */
  formula?: string;
};

const styles = StyleSheet.create({
  mark: {
    width: 20,
    height: 20,
    borderRadius: radii.pill,
    alignItems: 'center',
    justifyContent: 'center',
  },
  entry: {gap: 2, paddingVertical: spacing(2)},
  formula: {
    marginTop: spacing(1),
    alignSelf: 'flex-start',
    paddingHorizontal: spacing(2),
    paddingVertical: spacing(1),
    borderRadius: radii.sm,
  },
  note: {flexDirection: 'row', alignItems: 'flex-start', gap: spacing(2)},
  noteText: {flex: 1},
});

/** One term in an explanation sheet. */
function Entry({definition, last}: {definition: Definition; last: boolean}) {
  const p = useTheme();
  return (
    <View
      style={[
        styles.entry,
        !last && {borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: p.border},
      ]}>
      <AppText variant="subtitle">{definition.term}</AppText>
      <AppText variant="body" color={p.mutedForeground}>
        {definition.meaning}
      </AppText>
      {definition.formula ? (
        // The arithmetic gets the machine face and its own wash: it is the answer to
        // "where did this figure come from", and it must not read as more prose.
        <View style={[styles.formula, {backgroundColor: p.muted}]}>
          <AppText variant="mono">{definition.formula}</AppText>
        </View>
      ) : null}
    </View>
  );
}

/**
 * The `?` beside a heading, and the sheet it opens.
 *
 * Self-contained on purpose: a screen adds one element and gains an explanation, with
 * no visibility state to thread through it. Fifteen of these across the report screens
 * would otherwise be fifteen `useState`s in three page components.
 */
export function Explain({
  title,
  subtitle,
  definitions,
  /** A closing line under the definitions — a caveat, or where to change the setting. */
  note,
  style,
  testID,
}: {
  title: string;
  subtitle?: string;
  definitions: readonly Definition[];
  note?: string;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t('reports.explainAria', {title})}
        onPress={() => setOpen(true)}
        hitSlop={12}
        testID={testID}
        style={({pressed}) => [
          styles.mark,
          {backgroundColor: pressed ? p.accent : withAlpha(p.mutedForeground, 0.12)},
          style,
        ]}>
        <Icon name="info" size={15} color={p.mutedForeground} />
      </Pressable>

      <Sheet
        visible={open}
        onClose={() => setOpen(false)}
        title={title}
        subtitle={subtitle}
        testID={testID ? `${testID}-sheet` : undefined}>
        {definitions.map((definition, i) => (
          <Entry
            key={definition.term}
            definition={definition}
            last={i === definitions.length - 1 && !note}
          />
        ))}
        {note ? (
          <View style={[styles.entry, styles.note]}>
            <Icon name="info" size={16} color={p.mutedForeground} />
            <AppText variant="caption" style={styles.noteText}>
              {note}
            </AppText>
          </View>
        ) : null}
      </Sheet>
    </>
  );
}

/**
 * A quiet line that explains the block above it, with the same glyph the `?` uses.
 *
 * For the explanations too short to be worth a sheet and too important to leave out —
 * "grey means zero", "tap a column for that day's figures". A caption on its own reads
 * as a footnote nobody looks at; the glyph is what marks it as help.
 */
export function HelpNote({
  text,
  style,
  testID,
}: {
  text: string;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}) {
  const p = useTheme();
  return (
    <View testID={testID} style={[styles.note, style]}>
      <Icon name="info" size={14} color={p.mutedForeground} />
      <AppText variant="caption" style={styles.noteText}>
        {text}
      </AppText>
    </View>
  );
}
